import { useQuery } from '@apollo/client'
import { AppBar, Tab, Tabs, useTheme } from '@material-ui/core'
import { useMemo } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { buildFeatureSet, canAccessTab, getCredentials } from '../../cache/auth'
import Breadcrumbs from '../../components/Breadcrumb'
import { PROVIDER_QUERY } from '../../graphql/providerQueries'
import { TabPanel } from '../../helpers/TabPanel'
import { useRegisterSupportScreen } from '../../helpers/supportScreenContext'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import providerPagesRoleManagement from '../../staticRoleManagement/providerRoleManagement'
import ProviderForm from '../ProviderForm'
import { UploadBoxForProviderLogo } from '../ProviderUploadLogo'
import useStyles from './styles/index.styles'

const ProviderTabs = () => {
  const classes = useStyles()
  const theme = useTheme()
  const { providerID, providerCode, page } = useParams<{
    providerID: string
    providerCode: string
    page: string
  }>()

  const { data, loading, error } = useQuery<{
    provider: { name: string; providerCode: string }
  }>(PROVIDER_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      providerCode: providerCode
    }
  })

  useRegisterSupportScreen({
    affectedEntities:
      !loading && !error && data?.provider?.providerCode
        ? [
            {
              type: 'provider',
              code: data.provider.providerCode,
              label: data.provider.name
            }
          ]
        : []
  })
  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Providers'), link: '/provider' },
      {
        label: data?.provider.name || ''
      }
    ],
    [data]
  )

  const tabsList = [
    {
      disableRipple: true,
      value: 'details',
      className: classes.tab,
      label: getTranslate('Details'),
      link: `/provider/${providerID}/${providerCode}/details`,
      component: LocalizedLink
    },
    {
      disableRipple: false,
      value: 'logo',
      className: classes.tab,
      label: getTranslate('Logo'),
      link: `/provider/${providerID}/${providerCode}/logo`,
      component: LocalizedLink
    }
  ]

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const routeConfig = providerPagesRoleManagement.find(
    (item) => item.route === '/provider/:providerID/:providerCode/:page'
  )

  const visibleTabs = tabsList.filter((tab) =>
    canAccessTab(routeConfig, tab.value, featureSet)
  )

  if (!canAccessTab(routeConfig, page, featureSet)) {
    return <Redirect to="/403" />
  }

  return (
    <div className={classes.root}>
      <AppBar className={classes.appBar} position="static" color="transparent">
        <Breadcrumbs levels={cachedLevels} loading={loading} />
        <Tabs
          value={page}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto">
          {visibleTabs.map((tabItem) => (
            <Tab
              key={tabItem.value}
              disableRipple={tabItem.disableRipple}
              value={tabItem.value}
              className={tabItem.className}
              label={tabItem.label}
              to={tabItem.link}
              component={LocalizedLink}
            />
          ))}
        </Tabs>
      </AppBar>
      <TabPanel
        value={page}
        index="details"
        dir={theme.direction}
        className={classes.tabPanel}>
        <ProviderForm />
      </TabPanel>
      <TabPanel
        value={page}
        index="logo"
        dir={theme.direction}
        className={classes.tabPanel}>
        <UploadBoxForProviderLogo />
      </TabPanel>
    </div>
  )
}
export default ProviderTabs
